/**
 * Admin Moderation Console — /admin/moderation (V9.4)
 *
 * Vue centralisée pour les admins / COPF :
 *  - tableau filtrable de toutes les entités (athlète, club, coach, fédération,
 *    compétition, curator/actualité, aide, événement calendrier) ;
 *  - filtres : type, statut (publié / dépublié / archivé / supprimé / etc.),
 *    nom, discipline, owner, date_from, date_to ;
 *  - actions : Voir relations · Dépublier · Archiver · Supprimer définitivement
 *    (confirmation forte « SUPPRIMER » + raison obligatoire) · Restaurer ;
 *  - chaque action passe par les endpoints existants `/api/admin/entities/...`
 *    (audit_logs côté backend déjà en place).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { adminMailto, getAdminEmail } from "@/lib/adminContact";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import {
  ShieldAlert, ShieldCheck, Search, RefreshCw, Eye, EyeOff, Archive,
  Trash2, RotateCcw, Network, Loader2, ExternalLink, FileText, ArrowLeft,
  UserCog,
} from "lucide-react";
import { RoleEditor } from "@/components/RoleEditor";

// -------------------------------------------------------------------------
//  Référentiels
// -------------------------------------------------------------------------
const ENTITIES = [
  { value: "all",                 label: "Toutes" },
  { value: "athlete",             label: "Athlètes" },
  { value: "club",                label: "Clubs" },
  { value: "coach",               label: "Coaches" },
  { value: "federation",          label: "Fédérations" },
  { value: "competition",         label: "Compétitions" },
  { value: "curator_item",        label: "Actualités (curator)" },
  { value: "funding_opportunity", label: "Aides aux sportifs" },
  { value: "calendar_event",      label: "Événements calendrier" },
];

const STATUSES = [
  { value: "all",         label: "Tous" },
  { value: "published",   label: "Publié" },
  { value: "unpublished", label: "Dépublié" },
  { value: "archived",    label: "Archivé" },
  { value: "deleted",     label: "Supprimé doucement" },
  { value: "rejected",    label: "Refusé" },
  { value: "proposed",    label: "Proposé" },
  { value: "validated",   label: "Validé" },
  { value: "draft",       label: "Brouillon" },
];

const STATUS_TONE = {
  published:   "default",
  unpublished: "secondary",
  archived:    "outline",
  deleted:     "destructive",
  rejected:    "destructive",
  proposed:    "secondary",
  validated:   "default",
  draft:       "outline",
};

const ENTITY_LABEL = Object.fromEntries(ENTITIES.map((e) => [e.value, e.label]));

const DETAIL_PATH = {
  athlete:             (it) => it.slug ? `/athletes/${it.slug}` : `/athletes/${it.id}`,
  club:                (it) => it.slug ? `/clubs/${it.slug}` : `/clubs/${it.id}`,
  coach:               (it) => it.slug ? `/coachs/${it.slug}` : `/coachs/${it.id}`,
  federation:          (it) => it.slug ? `/federations/${it.slug}` : `/federations/${it.id}`,
  competition:         (it) => it.slug ? `/competitions/${it.slug}` : `/competitions/${it.id}`,
  curator_item:        (_) => `/actualites`,
  funding_opportunity: (_) => `/aides-sportifs`,
  calendar_event:      (_) => `/calendrier`,
};

// -------------------------------------------------------------------------
//  Page
// -------------------------------------------------------------------------
export default function AdminModerationPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));

  // ---- Filtres ----
  // V11.2 — préremplit `entity` depuis l'URL (ex: /admin/moderation?entity=athlete)
  const [entity, setEntity] = useState(() => searchParams.get("entity") || "all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [owner, setOwner] = useState("");
  const [dateField, setDateField] = useState("updated_at");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // ---- Pagination ----
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);

  // ---- Données ----
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalsByStatus, setTotalsByStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // ---- Dialogs ----
  const [actionDialog, setActionDialog] = useState(null);    // { item, kind: 'unpublish'|'archive'|'delete'|'restore' }
  const [relationsDialog, setRelationsDialog] = useState(null); // { item, data, loading }
  const [rolesDialog, setRolesDialog] = useState(null); // { userId, entityName }

  // V11 Hotfix — Bulk select
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const keyFor = (it) => `${it.entity}::${it.id}`;
  const toggleOne = (it) => setSelectedKeys((s) => { const n = new Set(s); const k = keyFor(it); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleAll = () => setSelectedKeys((s) => {
    const allKeys = items.map(keyFor);
    const allSelected = allKeys.every((k) => s.has(k));
    if (allSelected) return new Set();
    return new Set(allKeys);
  });
  const runBulk = async (kind) => {
    if (selectedKeys.size === 0) return;
    if (!window.confirm(`Confirmer ${kind} sur ${selectedKeys.size} élément(s) ?`)) return;
    setBulkBusy(true);
    try {
      const items_payload = Array.from(selectedKeys).map((k) => {
        const [entity, id] = k.split("::");
        return { entity, id };
      });
      const { data } = await api.post("/admin/entities/bulk", { kind, items: items_payload, reason: "bulk_moderation" });
      toast.success(`${data.success} traité(s), ${data.failed.length} échec(s)`);
      setSelectedKeys(new Set());
      setRefreshKey((k) => k + 1);
    } catch (e) {
      showError(e, { prefix: "Bulk :" });
    } finally { setBulkBusy(false); }
  };

  const fetchList = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const params = {
        entity, status,
        date_field: dateField,
        limit, offset,
      };
      if (q.trim()) params.q = q.trim();
      if (discipline.trim()) params.discipline = discipline.trim();
      if (owner.trim()) params.owner = owner.trim();
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo)   params.date_to   = new Date(dateTo + "T23:59:59").toISOString();
      const { data } = await api.get("/admin/moderation/list", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalsByStatus(data.totals_by_status || {});
    } catch (e) {
      showError(e, { prefix: "Modération :" });
    } finally {
      setLoading(false);
    }
  }, [isAdmin, entity, status, q, discipline, owner, dateField, dateFrom, dateTo, limit, offset, refreshKey]);  // eslint-disable-line

  useEffect(() => { fetchList(); }, [fetchList]);

  // Reset pagination quand on change un filtre principal
  useEffect(() => { setOffset(0); }, [entity, status, q, discipline, owner, dateField, dateFrom, dateTo]);

  const refresh = () => setRefreshKey((k) => k + 1);

  // ---- Actions ----
  const openAction = (item, kind) => setActionDialog({ item, kind });
  const openRelations = async (item) => {
    setRelationsDialog({ item, data: null, loading: true });
    try {
      const { data } = await api.get(`/admin/entities/${item.entity}/${item.id}/relations`);
      setRelationsDialog({ item, data, loading: false });
    } catch (e) {
      showError(e, { prefix: "Relations :" });
      setRelationsDialog(null);
    }
  };

  // ---- Permissions ----
  if (!user) {
    return (
      <div className="mx-auto max-w-3xl p-8" data-testid="moderation-need-login">
        <Card className="p-6 text-center">Connecte-toi pour accéder à la modération.</Card>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl p-8" data-testid="moderation-forbidden">
        <Card className="p-6">
          <ShieldAlert className="h-6 w-6 text-[hsl(var(--aito-energy))]" />
          <h2 className="font-display text-xl font-bold mt-2">Accès administrateur</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Ton compte n'a pas les droits administrateur. Contacte <a href={adminMailto({ subject: "Demande d'accès Modération" })} className="underline">{getAdminEmail()}</a>.
          </p>
        </Card>
      </div>
    );
  }

  // ---- Render ----
  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-6 space-y-4" data-testid="admin-moderation-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => nav("/admin")} data-testid="moderation-back-admin">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" />Admin
            </Button>
            <span>/ Modération</span>
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2 mt-1">
            <ShieldCheck className="h-7 w-7 text-[hsl(var(--aito-lagoon))]" />
            Console de modération
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Vue centralisée de tout le contenu publié. Dépublier · Archiver · Supprimer définitivement · Restaurer.
            Les actions sont auditées dans <code>audit_logs</code>.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="moderation-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Rafraîchir
        </Button>
      </div>

      {/* Compteurs par statut */}
      {Object.keys(totalsByStatus).length > 0 && (
        <Card className="p-3" data-testid="moderation-counters">
          <div className="flex flex-wrap gap-2 text-xs">
            {STATUSES.filter((s) => s.value !== "all" && (totalsByStatus[s.value] || 0) > 0).map((s) => (
              <button
                key={s.value}
                onClick={() => setStatus(s.value)}
                data-testid={`moderation-counter-${s.value}`}
                className={`px-2.5 py-1 rounded-md border transition ${
                  status === s.value
                    ? "border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon))]/10 text-foreground"
                    : "border-border hover:bg-muted"
                }`}
              >
                <span className="font-medium">{s.label}</span>
                <span className="ml-1.5 font-bold">{totalsByStatus[s.value] || 0}</span>
              </button>
            ))}
            <button
              onClick={() => setStatus("all")}
              data-testid="moderation-counter-all"
              className={`px-2.5 py-1 rounded-md border transition ${
                status === "all"
                  ? "border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon))]/10"
                  : "border-border hover:bg-muted"
              }`}
            >
              <span className="font-medium">Tous statuts</span>
            </button>
          </div>
        </Card>
      )}

      {/* Filtres */}
      <Card className="p-4 space-y-3" data-testid="moderation-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Type d'entité</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger data-testid="moderation-filter-entity"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ENTITIES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Statut</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="moderation-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">Recherche (nom · titre · slug)</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="ex : anna, jiu-jitsu, copf..."
                className="pl-8"
                data-testid="moderation-filter-q"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Discipline</Label>
            <Input value={discipline} onChange={(e) => setDiscipline(e.target.value)} placeholder="ex : surf, va'a" data-testid="moderation-filter-discipline" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Owner (email ou user_id)</Label>
            <Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="ex : admin@example.com" data-testid="moderation-filter-owner" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Champ date</Label>
            <Select value={dateField} onValueChange={setDateField}>
              <SelectTrigger data-testid="moderation-filter-date-field"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="updated_at">Dernière modification</SelectItem>
                <SelectItem value="created_at">Création</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Du</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="moderation-filter-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Au</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="moderation-filter-date-to" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={() => {
            setEntity("all"); setStatus("all"); setQ(""); setDiscipline("");
            setOwner(""); setDateField("updated_at"); setDateFrom(""); setDateTo("");
          }} data-testid="moderation-filters-reset">
            Réinitialiser
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden" data-testid="moderation-table-card">
        <div className="px-4 py-2 border-b flex items-center justify-between text-xs text-muted-foreground gap-2 flex-wrap">
          <span data-testid="moderation-total">{total} résultat{total > 1 ? "s" : ""}</span>
          {selectedKeys.size > 0 && (
            <div className="flex items-center gap-2" data-testid="moderation-bulk-bar">
              <span className="font-medium text-foreground">{selectedKeys.size} sélectionné(s)</span>
              <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => runBulk("unpublish")} data-testid="moderation-bulk-unpublish">Dépublier</Button>
              <Button size="sm" variant="outline" disabled={bulkBusy} onClick={() => runBulk("archive")} data-testid="moderation-bulk-archive">Archiver</Button>
              <Button size="sm" variant="ghost" disabled={bulkBusy} onClick={() => setSelectedKeys(new Set())}>Annuler</Button>
            </div>
          )}
          <span>{offset + 1}–{Math.min(offset + limit, total)}</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-6">
                  <input
                    type="checkbox"
                    checked={items.length > 0 && items.every((it) => selectedKeys.has(keyFor(it)))}
                    onChange={toggleAll}
                    className="h-4 w-4 accent-[hsl(var(--aito-lagoon))]"
                    data-testid="moderation-select-all"
                  />
                </TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Nom / titre</TableHead>
                <TableHead>Discipline</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Maj</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {Array.from({ length: 8 }).map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-sm text-muted-foreground" data-testid="moderation-empty">
                    Aucun contenu ne correspond à ces filtres. Essayez de modifier vos critères.
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.map((it) => (
                <ModerationRow
                  key={`${it.entity}-${it.id}`}
                  item={it}
                  onAction={(kind) => openAction(it, kind)}
                  onRelations={() => openRelations(it)}
                  onEditRoles={() => setRolesDialog({ userId: it.owner_user_id, entityName: it.name })}
                  selected={selectedKeys.has(keyFor(it))}
                  onToggle={() => toggleOne(it)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Pagination */}
        {total > limit && (
          <div className="px-4 py-3 border-t flex items-center justify-between text-xs">
            <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} data-testid="moderation-page-prev">
              ← Précédent
            </Button>
            <span>Page {Math.floor(offset / limit) + 1} / {Math.max(1, Math.ceil(total / limit))}</span>
            <Button size="sm" variant="outline" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} data-testid="moderation-page-next">
              Suivant →
            </Button>
          </div>
        )}
      </Card>

      {/* Action dialog */}
      {actionDialog && (
        <ActionDialog
          open={!!actionDialog}
          onOpenChange={(v) => { if (!v) setActionDialog(null); }}
          item={actionDialog.item}
          kind={actionDialog.kind}
          onDone={() => { setActionDialog(null); refresh(); }}
        />
      )}

      {/* Relations dialog */}
      {relationsDialog && (
        <RelationsDialog
          open={!!relationsDialog}
          onOpenChange={(v) => { if (!v) setRelationsDialog(null); }}
          item={relationsDialog.item}
          data={relationsDialog.data}
          loading={relationsDialog.loading}
        />
      )}

      {/* Roles editor dialog */}
      {rolesDialog && (
        <Dialog open={!!rolesDialog} onOpenChange={(v) => { if (!v) setRolesDialog(null); }}>
          <DialogContent data-testid="moderation-dialog-roles" className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCog className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
                Modifier les rôles — {rolesDialog.entityName}
              </DialogTitle>
              <DialogDescription>
                Multi-rôle autorisé (Athlete + Coach). Un seul endpoint applique le changement partout : annuaires, sélecteurs, fiches publiques.
              </DialogDescription>
            </DialogHeader>
            <RoleEditor
              userId={rolesDialog.userId}
              onSaved={() => { setRolesDialog(null); refresh(); }}
              hideHeader
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
//  Ligne du tableau
// -------------------------------------------------------------------------
function ModerationRow({ item, onAction, onRelations, onEditRoles, selected, onToggle }) {
  const detail = DETAIL_PATH[item.entity]?.(item) || "#";
  const dateMaj = item.updated_at ? new Date(item.updated_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";
  const tone = STATUS_TONE[item.status] || "secondary";
  const canUnpublish = !["unpublished", "archived", "deleted"].includes(item.status);
  const canArchive   = !["archived", "deleted"].includes(item.status);
  const canDelete    = item.status !== "deleted";
  const canRestore   = ["archived", "deleted", "unpublished"].includes(item.status);
  const canEditRoles = ["athlete", "coach"].includes(item.entity) && !!item.owner_user_id;

  return (
    <TableRow data-testid={`moderation-row-${item.entity}-${item.id}`}>
      <TableCell>
        <input
          type="checkbox"
          checked={!!selected}
          onChange={onToggle}
          className="h-4 w-4 accent-[hsl(var(--aito-lagoon))]"
          data-testid={`moderation-select-${item.id}`}
        />
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-[10px]">{ENTITY_LABEL[item.entity] || item.entity}</Badge>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <a
            href={detail}
            target="_blank" rel="noopener noreferrer"
            className="font-medium hover:underline truncate max-w-[26ch] inline-block"
            data-testid={`moderation-name-${item.id}`}
            title={item.name}
          >
            {item.name}
          </a>
          <a href={detail} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        {item.slug && <div className="text-[10px] text-muted-foreground truncate max-w-[26ch]">{item.slug}</div>}
      </TableCell>
      <TableCell className="text-xs">{item.discipline || "—"}</TableCell>
      <TableCell>
        <Badge variant={tone} data-testid={`moderation-status-${item.id}`} className="capitalize text-[10px]">
          {(STATUSES.find((s) => s.value === item.status) || {}).label || item.status}
        </Badge>
      </TableCell>
      <TableCell className="text-xs">
        {item.owner_email || item.owner_user_id ? (
          <span className="truncate max-w-[18ch] inline-block" title={item.owner_email || item.owner_user_id}>
            {item.owner_email || item.owner_user_id?.slice(0, 8) + "…"}
          </span>
        ) : "—"}
      </TableCell>
      <TableCell className="text-xs whitespace-nowrap">{dateMaj}</TableCell>
      <TableCell className="text-right">
        <div className="inline-flex items-center gap-1 flex-wrap justify-end">
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onRelations} title="Voir les relations" data-testid={`moderation-relations-${item.id}`}>
            <Network className="h-3.5 w-3.5" />
          </Button>
          {canEditRoles && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-[hsl(var(--aito-lagoon))] hover:text-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon))]/10"
              onClick={onEditRoles}
              title="Modifier les rôles du profil (Athlete/Coach)"
              data-testid={`moderation-edit-roles-${item.id}`}
            >
              <UserCog className="h-3.5 w-3.5" />
            </Button>
          )}
          {canUnpublish && (
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onAction("unpublish")} title="Dépublier" data-testid={`moderation-unpublish-${item.id}`}>
              <EyeOff className="h-3.5 w-3.5" />
            </Button>
          )}
          {canArchive && (
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onAction("archive")} title="Archiver" data-testid={`moderation-archive-${item.id}`}>
              <Archive className="h-3.5 w-3.5" />
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => onAction("delete")} title="Supprimer définitivement" data-testid={`moderation-delete-${item.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
          {canRestore && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => onAction("restore")} title="Restaurer" data-testid={`moderation-restore-${item.id}`}>
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// -------------------------------------------------------------------------
//  Dialog d'action (unpublish / archive / delete / restore)
// -------------------------------------------------------------------------
const ACTION_META = {
  unpublish: { title: "Dépublier", verb: "dépublier", icon: EyeOff,  tone: "default",    reasonRequired: false, needsHardConfirm: false },
  archive:   { title: "Archiver",  verb: "archiver",  icon: Archive, tone: "default",    reasonRequired: false, needsHardConfirm: false },
  delete:    { title: "Supprimer définitivement", verb: "supprimer définitivement", icon: Trash2, tone: "destructive", reasonRequired: true,  needsHardConfirm: true },
  restore:   { title: "Restaurer", verb: "restaurer", icon: RotateCcw, tone: "default",  reasonRequired: false, needsHardConfirm: false },
};

function ActionDialog({ open, onOpenChange, item, kind, onDone }) {
  const meta = ACTION_META[kind];
  const Icon = meta.icon;
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [relations, setRelations] = useState(null);

  // Charge les relations à l'ouverture pour les conséquences
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (kind === "restore") return;
      try {
        const { data } = await api.get(`/admin/entities/${item.entity}/${item.id}/relations`);
        if (!cancelled) setRelations(data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [item.entity, item.id, kind]);

  const canConfirm = useMemo(() => {
    if (busy) return false;
    if (meta.reasonRequired && !reason.trim()) return false;
    if (meta.needsHardConfirm && confirmText !== "SUPPRIMER") return false;
    return true;
  }, [busy, meta, reason, confirmText]);

  const submit = async () => {
    setBusy(true);
    try {
      if (kind === "delete") {
        await api.delete(`/admin/entities/${item.entity}/${item.id}`, {
          params: { confirm: "SUPPRIMER", reason: reason || undefined },
        });
        toast.success("Élément supprimé définitivement.");
      } else if (kind === "restore") {
        await api.post(`/admin/entities/${item.entity}/${item.id}/restore`, {});
        toast.success("Élément restauré.");
      } else {
        await api.post(`/admin/entities/${item.entity}/${item.id}/${kind}`, { reason: reason || undefined });
        toast.success(kind === "archive" ? "Élément archivé." : "Élément dépublié.");
      }
      onDone?.();
    } catch (e) {
      showError(e, { prefix: `${meta.title} :` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`moderation-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${kind === "delete" ? "text-destructive" : ""}`} />
            {meta.title}
          </DialogTitle>
          <DialogDescription>
            Action <strong>{meta.verb}</strong> sur cet élément. Les actions sont auditées dans <code>audit_logs</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* Identité */}
          <div className="rounded-md border p-3 bg-muted/30 space-y-1.5">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">{ENTITY_LABEL[item.entity] || item.entity}</Badge>
              <Badge variant={STATUS_TONE[item.status] || "secondary"} className="text-[10px] capitalize">
                {(STATUSES.find((s) => s.value === item.status) || {}).label || item.status}
              </Badge>
            </div>
            <div className="font-medium" data-testid="moderation-dialog-name">{item.name}</div>
            {item.slug && <div className="text-[11px] text-muted-foreground">{item.slug}</div>}
            {item.owner_email && <div className="text-[11px] text-muted-foreground">Owner : {item.owner_email}</div>}
          </div>

          {/* Relations / conséquences */}
          {kind !== "restore" && (
            <div className="rounded-md border p-3 space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs font-semibold">
                <Network className="h-3.5 w-3.5" />Éléments liés
              </div>
              {!relations && <div className="text-xs text-muted-foreground">Chargement…</div>}
              {relations && (
                <ul className="text-xs space-y-0.5">
                  {Object.entries(relations.counts || {}).map(([k, v]) => (
                    <li key={k} className="flex justify-between">
                      <span>{k.replaceAll("_", " ")}</span>
                      <span className="font-mono font-bold">{v}</span>
                    </li>
                  ))}
                  {Object.keys(relations.counts || {}).length === 0 && (
                    <li className="text-muted-foreground">Aucune relation détectée.</li>
                  )}
                </ul>
              )}
              {kind === "delete" && (
                <p className="text-[11px] text-destructive pt-1.5">
                  ⚠️ Conséquence : l'entité passera <strong>is_deleted=true</strong> et <strong>archived=true</strong>. Elle disparaît du public et des partages sociaux (404 OG). La purge physique reste manuelle (DBA).
                </p>
              )}
              {kind === "archive" && (
                <p className="text-[11px] text-muted-foreground pt-1.5">
                  Conséquence : l'entité passe <strong>archived=true</strong>. Plus visible publiquement, mais restaurable à tout moment.
                </p>
              )}
              {kind === "unpublish" && (
                <p className="text-[11px] text-muted-foreground pt-1.5">
                  Conséquence : l'entité passe <strong>unpublished_at=now</strong>. Reste visible en admin, mais retirée du public et des partages OG.
                </p>
              )}
            </div>
          )}

          {/* Raison */}
          {kind !== "restore" && (
            <div className="space-y-1">
              <Label className="text-xs">
                Raison {meta.reasonRequired && <span className="text-destructive">*</span>}
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={meta.reasonRequired ? "Obligatoire — ex : Demande RGPD du propriétaire, doublon, contenu hors-sujet…" : "Optionnel — sera enregistrée dans audit_logs"}
                rows={3}
                data-testid="moderation-dialog-reason"
              />
            </div>
          )}

          {/* Confirmation forte SUPPRIMER */}
          {meta.needsHardConfirm && (
            <div className="space-y-1">
              <Label className="text-xs text-destructive">
                Pour confirmer, tape exactement <code className="bg-muted px-1 rounded">SUPPRIMER</code>
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="SUPPRIMER"
                className={confirmText && confirmText !== "SUPPRIMER" ? "border-destructive" : ""}
                data-testid="moderation-dialog-confirm-input"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="moderation-dialog-cancel">Annuler</Button>
          <Button
            variant={meta.tone === "destructive" ? "destructive" : "default"}
            onClick={submit}
            disabled={!canConfirm}
            data-testid="moderation-dialog-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Icon className="h-4 w-4 mr-1.5" />}
            {meta.title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------------------------------
//  Dialog "Voir les relations" (lecture seule)
// -------------------------------------------------------------------------
function RelationsDialog({ open, onOpenChange, item, data, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="moderation-dialog-relations">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Network className="h-5 w-5" />Relations de {item.name}
          </DialogTitle>
          <DialogDescription>
            Vue des rattachements existants avant un archivage ou une suppression.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          {loading && <div className="text-xs text-muted-foreground">Chargement…</div>}
          {!loading && data && (
            <ul className="space-y-1">
              {Object.entries(data.counts || {}).map(([k, v]) => (
                <li key={k} className="flex justify-between border-b pb-1">
                  <span>{k.replaceAll("_", " ")}</span>
                  <span className="font-mono font-bold">{v}</span>
                </li>
              ))}
              {Object.keys(data.counts || {}).length === 0 && (
                <li className="text-muted-foreground">Aucune relation détectée.</li>
              )}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="moderation-dialog-relations-close">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
